/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChartTooltip } from "@/components/ui/chart/ChartTooltip";
import ToolTipBase from "@/components/ui/tooltip";
import { getColorPalette, getYAxisWidth } from "@/helpers/chartHelper";
import { formatMeasure } from "@/utils/format";

import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

export type HorizontalGroupedBarChartData = {
  name: string;
  [key: string]: any;
};

type HorizontalGroupedBarChartProps = {
  rawData: HorizontalGroupedBarChartData[];
};

const BAR_HEIGHT_ONE_SERIES = 50;
const BAR_HEIGHT_MULTIPLE_SERIES = 20;
const MIN_CHART_HEIGHT = 400; // Minimum height of the chart

export const YAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const maxLength = 12;
  const fullLabel = payload.value;
  const displayLabel =
    fullLabel.length > maxLength
      ? `${fullLabel.slice(0, maxLength)}...`
      : fullLabel;

  return (
    <g transform={`translate(${x + 5},${y})`}>
      {fullLabel.length > maxLength ? (
        <ToolTipBase content={fullLabel} position="top-center">
          <text
            x={0}
            y={0}
            dy={4}
            textAnchor="end"
            fill="#000000B2"
            fontSize={12}
            style={{
              cursor: "pointer"
            }}
          >
            {displayLabel}
          </text>
        </ToolTipBase>
      ) : (
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          fill="#000000B2"
          fontSize={12}
          style={{
            cursor: "default"
          }}
        >
          {displayLabel}
        </text>
      )}
    </g>
  );
};

const HorizontalGroupedBarChart = ({
  rawData = []
}: HorizontalGroupedBarChartProps) => {
  // List of keys for series, taken from the first data row's "data" properties
  const seriesKeys = rawData.length
    ? Object.keys(rawData[0]).filter((k) => k !== "name")
    : [];

  // Generate as many colors as needed for this data set
  const COLORS = getColorPalette(seriesKeys.length || 1);

  const totalValue = rawData.reduce((sum, item) => {
    Object.keys(item).forEach((key) => {
      if (typeof item[key] === "number") sum += item[key];
    });
    return sum;
  }, 0);

  const maxValue = Math.max(
    ...rawData.flatMap((item) =>
      Object.values(item).filter((val) => typeof val === "number")
    )
  );

  const renderLegendText = (value: any, _entry: any, _index: number) => (
    <span className="text-xs text-black text-opacity-70">{value}</span>
  );

  if (!rawData?.length || totalValue === 0)
    return (
      <div className="flex-center h-[400px] px-4">
        <p className="text-base text-txtHeading font-semibold text-center">
          No data available to display. Please check your data or try adjusting
          the configuration menu.
        </p>
      </div>
    );

  // Bar height is determined by the number of series keys
  const barHeight =
    seriesKeys?.length > 1
      ? seriesKeys.length * BAR_HEIGHT_MULTIPLE_SERIES
      : BAR_HEIGHT_ONE_SERIES;

  const heightOfChart = Math.max(rawData.length * barHeight, MIN_CHART_HEIGHT);

  const radiusBar = seriesKeys?.length === 1 ? 30 : 10;

  const maxLabelWidth = getYAxisWidth(rawData, "12px", 12);

  const yAxisWidth = Math.ceil(maxLabelWidth) + 24;

  return (
    <ResponsiveContainer width="100%" height={heightOfChart}>
      <BarChart
        data={rawData}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 0, bottom: 10 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          axisLine={false}
          tickLine={false}
          fontSize={12}
          color="#000000B2"
          domain={[0, maxValue * 1.3]}
          tickFormatter={(value: number) => formatMeasure(value, "chart")}
        />
        <YAxis
          type="category"
          dataKey="name"
          tickLine={false}
          axisLine={false}
          fontSize={12}
          color="#000000B2"
          tick={YAxisTick}
          width={yAxisWidth}
        />
        <Tooltip
          content={(content) => (
            <ChartTooltip {...content} totalValue={totalValue} />
          )}
        />
        <Legend
          verticalAlign="bottom"
          align="center"
          iconType="square"
          iconSize={8}
          wrapperStyle={{ marginTop: 10 }}
          formatter={renderLegendText}
        />
        {seriesKeys.map((key, idx) => (
          <Bar
            key={key}
            dataKey={key}
            fill={COLORS[idx % COLORS.length]}
            radius={[0, radiusBar, radiusBar, 0]}
            barSize={seriesKeys?.length === 1 ? 30 : 10}
          >
            <LabelList
              dataKey={key}
              position="right"
              fill="#000000B2"
              fontWeight={700}
              fontSize={12}
              formatter={(value: number | string) => {
                const formatted = formatMeasure(Number(value), "chart");
                return formatted === "0" ? "null" : formatted;
              }}
            />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default HorizontalGroupedBarChart;
